/**
 * Persists ?src= and ?intent= from /sign-up through email and Google OAuth so
 * PostHog signup events can be attributed to the marketing CTA that sent the
 * visitor. Values are stored in sessionStorage because the Google redirect
 * drops query params.
 */
export type SignupAttribution = {
  source?: string;
  intent?: string;
  landingAudience?: "writer" | "editor";
};

const STORAGE_KEY = "novilot_signup_attribution";
const PLACEMENT_SOURCES = new Set([
  "hero", "nav", "pricing-summary", "pricing-footer", "pricing-page", "content", "demo", "faq", "features-hub", "alt-scrivener", "alt-sudowrite", "writer-content", "for-fiction-writers-hero", "for-fiction-writers-final", "editor-content", "for-fiction-editors-final", "for-fiction-editors-hero", "sample-continuity-report", "google",
  "best-continuity", "compare-chatgpt", "compare-manual-bible", "compare-novelcrafter", "compare-scrivener", "compare-sudowrite", "for-fantasy-scifi", "for-indie", "for-romance", "for-serial-fiction", "howto-screens", "howto-create-tool", "howto-series-bible", "howto-feedback", "howto-char-consistent", "howto-navigate", "howto-organize", "howto-pin", "howto-search", "howto-start", "howto-characters", "product",
  ...["analysis", "beta", "character-tracking", "characters", "cloud-backup", "continuity", "copilot", "custom-tools", "import-export", "editor", "outline", "proofread", "publishing", "series", "story-bible", "timeline", "translation"].map(value => `feature-${value}`),
  ...["feature-editorial-preflight", "feature-fiction-continuity-audit", "feature-manuscript-analysis-for-editors", "feature-story-bible-for-editors", "feature-fiction-editor-style-sheet", "feature-manuscript-revision-comparison", "feature-editor-client-review", "comparison-novilot-vs-fictionary", "comparison-novilot-vs-plotlens", "comparison-novilot-and-perfectit", "comparison-novilot-vs-autocrit", "comparison-novilot-vs-prowritingaid", "guide-run-a-fiction-continuity-pass", "guide-check-character-consistency-across-a-novel", "guide-track-character-knowledge", "guide-use-ai-with-client-manuscripts", "guide-complete-your-first-client-review", "guide-create-a-fiction-style-sheet", "guide-review-a-revised-manuscript", "template-fiction-continuity-checklist", "template-developmental-editorial-report", "template-client-manuscript-intake", "template-editor-ai-consent-clause", "template-fiction-editing-style-sheet", "security-editor-data-processing"].map(value => `editor-${value}`),
]);

/** Only campaign codes we publish; never put a contact name or address in src. */
export function editorCampaignSource(source: string | undefined): string | undefined {
  return source && /^editor-campaign-(outreach|partner|community|workshop|social|referral|search)-w[1-8]$/.test(source)
    ? source
    : undefined;
}

/** Campaign codes are deliberately constrained; arbitrary URLs and click IDs are not captured. */
export function marketingCampaignSource(source: string | undefined): string | undefined {
  if (editorCampaignSource(source)) return source;
  if (source && /^writer-campaign-(partner|community|workshop|social|referral|search)-w[1-8]$/.test(source)) return source;
  return source && ["paid-google", "paid-meta", "paid-bing"].includes(source) ? source : undefined;
}

/** Only known placement and campaign codes may be sent to analytics or stored at signup. */
export function safeSignupSource(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const source = value.trim();
  return marketingCampaignSource(source) ?? (PLACEMENT_SOURCES.has(source) ? source : undefined);
}

export function safeReferrerOrigin(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  try { const url = new URL(value); return url.protocol === "https:" || url.protocol === "http:" ? url.origin : undefined; } catch { return undefined; }
}

export function safeLandingAudience(value: unknown): "writer" | "editor" | undefined {
  return value === "writer" || value === "editor" ? value : undefined;
}

/** Record the visited audience page separately from a later workspace choice. */
export function rememberLandingAudience(audience: "writer" | "editor"): void {
  if (typeof window === "undefined") return;
  const saved = readSignupAttribution();
  persistSignupAttribution({ ...saved, landingAudience: saved.landingAudience ?? audience });
}

/** Preserve a known campaign across marketing pages without collecting free-form query values. */
export function readMarketingCampaign(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams(window.location.search);
  const explicit = marketingCampaignSource(params.get("src") ?? undefined) ?? marketingCampaignSource(params.get("utm_campaign") ?? undefined);
  const medium = params.get("utm_medium")?.toLowerCase();
  const channel = params.get("utm_source")?.toLowerCase();
  const paid = medium && ["cpc", "ppc", "paid", "paid_search", "paid_social"].includes(medium)
    ? channel === "google" ? "paid-google" : channel === "meta" || channel === "facebook" || channel === "instagram" ? "paid-meta" : channel === "bing" ? "paid-bing" : undefined
    : undefined;
  let stored: SignupAttribution = {};
  try { stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") as SignupAttribution; } catch { /* Optional attribution. */ }
  const source = explicit ?? paid ?? marketingCampaignSource(stored.source);
  if (source && source !== stored.source) persistSignupAttribution({ ...stored, source });
  return source;
}

export function parseSignupAttributionFromSearch(search: string): SignupAttribution {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const source = safeSignupSource(params.get("src"));
  const requestedIntent = params.get("intent")?.trim();
  const intent = requestedIntent && ["writer", "editor", "both", "import", "write"].includes(requestedIntent) ? requestedIntent : undefined;
  return { source, intent };
}

export function persistSignupAttribution(attribution: SignupAttribution): void {
  if (typeof window === "undefined") return;
  const next: SignupAttribution = {};
  const source = safeSignupSource(attribution.source);
  if (source) next.source = source;
  if (attribution.intent && ["writer", "editor", "both", "import", "write"].includes(attribution.intent)) next.intent = attribution.intent;
  if (safeLandingAudience(attribution.landingAudience)) next.landingAudience = attribution.landingAudience;
  if (!next.source && !next.intent && !next.landingAudience) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* noop */
  }
}

export function readSignupAttribution(): SignupAttribution {
  if (typeof window === "undefined") return {};

  const fromUrl = parseSignupAttributionFromSearch(window.location.search);
  let stored: SignupAttribution = {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SignupAttribution;
      stored = { source: safeSignupSource(parsed.source), intent: parsed.intent && ["writer", "editor", "both", "import", "write"].includes(parsed.intent) ? parsed.intent : undefined, landingAudience: safeLandingAudience(parsed.landingAudience) };
    }
  } catch {
    /* noop */
  }

  const merged: SignupAttribution = {
    source: marketingCampaignSource(fromUrl.source) ?? marketingCampaignSource(stored.source) ?? fromUrl.source ?? stored.source,
    intent: fromUrl.intent ?? stored.intent,
    landingAudience: stored.landingAudience,
  };

  if (fromUrl.source || fromUrl.intent) {
    persistSignupAttribution(merged);
  }

  return merged;
}

export function clearSignupAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** PostHog-safe props for signup_started / signup_completed (no PII). */
export function signupAttributionCaptureProps(): Record<string, string> {
  const { source, intent, landingAudience } = readSignupAttribution();
  const props: Record<string, string> = {};
  if (source) props.source = source;
  if (intent) props.intent = intent;
  if (landingAudience) props.landing_audience = landingAudience;
  return props;
}
